import { LoadingButton } from '@mui/lab';
import { Box, Stack, Typography } from '@mui/material';
import { ResponsiveSideDialog } from 'common/components/ResponsiveSideDialog/ResponsiveSideDialog';
import { DialogComponentProps, useDialog } from 'common/hooks/useDialog';
import { useNotification } from 'common/hooks/useNotification';
import { usePeriodicityOptions } from 'common/hooks/usePeriodicityOptions';
import { formatCurrency } from 'common/utils/formatUtils';
import { format } from 'date-fns';
import { TransactionGroupPeriodTypeCode, useCreateCashFlowAdjustmentMutation } from 'generated/graphql';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DetailRow } from '../DetailRow/DetailRow';
import {
  EditCashFlowAdjustmentAmountDialog,
  EditCashFlowAdjustmentAmountDialogProps,
} from '../EditCashFlowAdjustmentAmountDialog/EditCashFlowAdjustmentAmountDialog';
import {
  EditCashFlowAdjustmentPeriodTypeDialog,
  EditCashFlowAdjustmentPeriodTypeDialogProps,
  PeriodType,
} from '../EditCashFlowAdjustmentPeriodTypeDialog/EditCashFlowAdjustmenPeriodTypeDialog';
import {
  EditCashFlowAdjustmentReferenceDateDialog,
  EditCashFlowAdjustmentReferenceDateDialogProps,
} from '../EditCashFlowAdjustmentReferenceDateDialog/EditCashFlowAdjustmentReferenceDateDialog';
import {
  EditCashFlowAdjustmentTextDialog,
  EditCashFlowAdjustmentTextDialogProps,
} from '../EditCashFlowAdjustmentTextDialog/EditCashFlowAdjustmentTextDialog';
import { styles } from './AddCashFlowAdjustmentDialog.styles';

export enum CashFlowAdjustmentType {
  Income = 'income',
  Expense = 'expense',
}

interface AddCashFlowAdjustmentProps {
  type: CashFlowAdjustmentType;
}

const AddCashFlowAdjustmentDialogContent: React.FC<AddCashFlowAdjustmentDialogProps> = ({ onResolve, type }) => {
  const { i18n, t } = useTranslation();
  const notify = useNotification();
  const [amount, setAmount] = useState<number>();
  const [periodTypeCode, setPeriodTypeCode] = useState<TransactionGroupPeriodTypeCode>();
  const [periodCount, setPeriodCount] = useState<number>();
  const [referenceDate, setReferenceDate] = useState<Date>();
  const [text, setText] = useState<string>();

  const periodicityOptions = usePeriodicityOptions();

  const cashFlowAdjustmentAmountDialog = useDialog<EditCashFlowAdjustmentAmountDialogProps, number>(
    EditCashFlowAdjustmentAmountDialog,
    {
      instanceId: `set-cash-flow-adjustment-amount`,
    },
  );

  const handleAmountClick = async () => {
    const newAmount = await cashFlowAdjustmentAmountDialog.create({ amount });
    setAmount(newAmount);
  };

  const cashFlowAdjustmentPeriodTypeDialog = useDialog<EditCashFlowAdjustmentPeriodTypeDialogProps, PeriodType>(
    EditCashFlowAdjustmentPeriodTypeDialog,
    {
      instanceId: `set-cash-flow-adjustment-period-type`,
    },
  );

  const handlePeriodTypeClick = async () => {
    const newPeriodType = await cashFlowAdjustmentPeriodTypeDialog.create({
      periodType: { periodCount, periodTypeCode },
    });
    setPeriodTypeCode(newPeriodType.periodTypeCode ?? TransactionGroupPeriodTypeCode.Month);
    setPeriodCount(newPeriodType.periodCount ?? 1);
  };

  const cashFlowAdjustmentReferenceDateDialog = useDialog<EditCashFlowAdjustmentReferenceDateDialogProps, Date>(
    EditCashFlowAdjustmentReferenceDateDialog,
    {
      instanceId: `set-cash-flow-adjustment-reference-date`,
    },
  );

  const handleReferenceDateClick = async () => {
    const newReferenceDate = await cashFlowAdjustmentReferenceDateDialog.create({ referenceDate });
    setReferenceDate(newReferenceDate);
  };

  const cashFlowAdjustmentTextDialog = useDialog<EditCashFlowAdjustmentTextDialogProps, string>(
    EditCashFlowAdjustmentTextDialog,
    {
      instanceId: `set-cash-flow-adjustment-text`,
    },
  );

  const handleTextClick = async () => {
    const newText = await cashFlowAdjustmentTextDialog.create({ text, type });
    setText(newText);
  };

  const [createCashFlowAdjustment, { loading }] = useCreateCashFlowAdjustmentMutation({
    onCompleted: () => {
      onResolve();
    },
    onError: () => {
      notify({ message: t('common:somethingWentWrong'), type: 'error' });
      onResolve();
    },
    refetchQueries: ['CashFlowAdjustments'],
  });

  const handleSubmit = async () => {
    if (!!amount && !!periodCount && !!periodTypeCode && !!referenceDate && !!text) {
      await createCashFlowAdjustment({
        variables: {
          cashFlowAdjustment: {
            period: {
              expectedAmount: {
                amount: (type == CashFlowAdjustmentType.Expense ? -amount : amount) ?? 0,
                currency: 'EUR',
              },
              periodCount,
              periodTypeCode,
              referenceDate,
            },
            text,
          },
        },
      });
    } else {
      notify({ message: t('common:somethingWentWrong'), type: 'error' });
      onResolve();
    }
  };

  return (
    <Stack>
      <Box textAlign="center">
        <Typography variant="headlineXL">
          {type == CashFlowAdjustmentType.Income
            ? t('fintelligenceReport.addCashFlowAdjustmentDialog.income.title')
            : t('fintelligenceReport.addCashFlowAdjustmentDialog.expense.title')}
        </Typography>
      </Box>

      <Stack sx={styles.container}>
        <DetailRow<number>
          formatter={(value: number) => formatCurrency(Math.abs(value), i18n.language)}
          label={t('fintelligenceReport.addCashFlowAdjustmentDialog.amount')}
          onClick={handleAmountClick}
          value={amount}
        />
        <DetailRow<string>
          label={t('fintelligenceReport.addCashFlowAdjustmentDialog.periodType')}
          onClick={handlePeriodTypeClick}
          value={
            periodicityOptions.find(
              (option) => option.value.periodCount === periodCount && option.value.periodTypeCode === periodTypeCode,
            )?.label
          }
        />
        <DetailRow<Date>
          formatter={(value: Date) => format(value, 'd. M. yyyy')}
          label={t('fintelligenceReport.addCashFlowAdjustmentDialog.referenceDate')}
          onClick={handleReferenceDateClick}
          value={referenceDate}
        />
        <DetailRow<string>
          label={
            type == CashFlowAdjustmentType.Income
              ? t('fintelligenceReport.addCashFlowAdjustmentDialog.income.text')
              : t('fintelligenceReport.addCashFlowAdjustmentDialog.expense.text')
          }
          onClick={handleTextClick}
          value={text}
        />
      </Stack>

      <Box mt={8}>
        <LoadingButton
          color="primary"
          disabled={!amount || !periodCount || !periodTypeCode || !referenceDate || !text}
          fullWidth
          loading={loading}
          onClick={handleSubmit}
          size="large"
          type="submit"
        >
          <Typography color="white" variant="bodyL">
            {type == CashFlowAdjustmentType.Income
              ? t('fintelligenceReport.addCashFlowAdjustmentDialog.income.button')
              : t('fintelligenceReport.addCashFlowAdjustmentDialog.expense.button')}
          </Typography>
        </LoadingButton>
      </Box>
    </Stack>
  );
};

export interface AddCashFlowAdjustmentDialogProps extends AddCashFlowAdjustmentProps, DialogComponentProps<void> {}

export const AddCashFlowAdjustmentDialog: React.FC<AddCashFlowAdjustmentDialogProps> = (props) => {
  return (
    <ResponsiveSideDialog dialogId={props.instanceId} isOpen={props.isOpen} onClose={props.onResolve}>
      <AddCashFlowAdjustmentDialogContent {...props} />
    </ResponsiveSideDialog>
  );
};
